import { Component, Inject, inject, HostListener } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { trigger, transition, style, animate, state } from '@angular/animations';

export interface ClassificacaoIterativaDTO {
  passos: ClassificacaoPassoDTO[];
  eliminados: CandidatoExcluidoDTO[];
  ausentes: CandidatoExcluidoDTO[];
  notaEliminados: string | null;
  notaAusentes: string | null;
}

export interface ClassificacaoPassoDTO {
  posicaoResultante: number;
  quadroExaminadores: QuadroExaminadorDTO[];
  contagem: ContagemIndicacaoDTO[];
  classificado: ClassificadoDTO | null;
  empatados: ClassificadoDTO[];
  empate: boolean;
  observacao: string | null;
}

export interface QuadroExaminadorDTO {
  comissaoId: number;
  nome: string;
  candidatos: CandidatoRankDTO[];
}

export interface CandidatoRankDTO {
  inscricaoId: number;
  nome: string;
  pcd: boolean;
}

export interface ContagemIndicacaoDTO {
  inscricaoId: number;
  nome: string;
  indicacoes: number;
  pcd: boolean;
}

export interface ClassificadoDTO {
  inscricaoId: number;
  nome: string;
  quantidadeIndicacoes: number;
  pcd: boolean;
  conceito: string | null;
  justificativaDesempate: string | null;
}

export interface CandidatoExcluidoDTO {
  nome: string;
  motivo: string;
  fase: string;
}

@Component({
  selector: 'app-visualizar-classificacao-iterativa',
  templateUrl: './visualizar-classificacao-iterativa.component.html',
  styleUrls: ['./visualizar-classificacao-iterativa.component.scss'],
  animations: [
    trigger('fadeSlide', [
      transition(':increment', [
        style({ opacity: 0, transform: 'translateX(60px)' }),
        animate('500ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 1, transform: 'translateX(0)' }))
      ]),
      transition(':decrement', [
        style({ opacity: 0, transform: 'translateX(-60px)' }),
        animate('500ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0', opacity: 0, overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: 1 })),
      transition('collapsed <=> expanded', [
        animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)')
      ])
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('400ms ease-out', style({ opacity: 1 }))
      ])
    ]),
    trigger('sidebarItem', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ])
  ]
})
export class VisualizarClassificacaoIterativaComponent {
  dados: ClassificacaoIterativaDTO;
  passoAtual = 0;
  classificadosAteAgora: { posicao: number; nome: string; pcd: boolean; conceito: string | null; justificativa: string | null }[] = [];
  detalhesExpandido: { [key: number]: boolean } = {};
  mostrarInfoExcluidos = false;

  constructor(
    public dialogRef: MatDialogRef<VisualizarClassificacaoIterativaComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { dados: ClassificacaoIterativaDTO }
  ) {
    this.dados = data.dados;
  }

  get totalPassos(): number {
    return this.dados.passos.length;
  }

  get passoCorrente(): ClassificacaoPassoDTO {
    return this.dados.passos[this.passoAtual];
  }

  get progresso(): number {
    return ((this.passoAtual + 1) / this.totalPassos) * 100;
  }

  get temExcluidos(): boolean {
    return (this.dados.eliminados?.length > 0) || (this.dados.ausentes?.length > 0);
  }

  get maxCandidatosPorExaminador(): number {
    if (!this.passoCorrente?.quadroExaminadores.length) return 0;
    return Math.max(...this.passoCorrente.quadroExaminadores.map(ex => ex.candidatos.length));
  }

  get posicaoArray(): number[] {
    return Array.from({ length: this.maxCandidatosPorExaminador }, (_, i) => i);
  }

  // --- Navegação por teclado ---

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.avancar();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.voltar();
    } else if (event.key === 'Escape') {
      this.fechar();
    }
  }

  // --- Navegação ---

  avancar(): void {
    if (this.passoAtual < this.totalPassos - 1) {
      this.adicionarClassificados(this.passoCorrente);
      this.passoAtual++;
      // Se chegou no último passo, adicionar também o resultado dele
      if (this.passoAtual === this.totalPassos - 1) {
        this.adicionarClassificados(this.passoCorrente);
      }
    }
  }

  voltar(): void {
    if (this.passoAtual > 0) {
      this.passoAtual--;
      this.recalcularClassificados();
    }
  }

  private adicionarClassificados(passo: ClassificacaoPassoDTO): void {
    if (passo.classificado) {
      this.classificadosAteAgora.push({
        posicao: passo.posicaoResultante,
        nome: passo.classificado.nome,
        pcd: passo.classificado.pcd,
        conceito: null,
        justificativa: null
      });
    } else if (passo.empatados?.length) {
      const empatadosOrdenados = [...passo.empatados].sort((a, b) => {
        if (!a.conceito && !b.conceito) return 0;
        if (!a.conceito) return 1;
        if (!b.conceito) return -1;
        return a.conceito.localeCompare(b.conceito);
      });

      empatadosOrdenados.forEach((e, i) => {
        this.classificadosAteAgora.push({
          posicao: passo.posicaoResultante + i,
          nome: e.nome,
          pcd: e.pcd,
          conceito: e.conceito,
          justificativa: e.justificativaDesempate
        });
      });
    }
  }

  private recalcularClassificados(): void {
    this.classificadosAteAgora = [];
    for (let i = 0; i < this.passoAtual; i++) {
      this.adicionarClassificados(this.dados.passos[i]);
    }
    // Se está no último passo, incluir também
    if (this.passoAtual === this.totalPassos - 1) {
      this.adicionarClassificados(this.dados.passos[this.passoAtual]);
    }
  }

  // --- Progressive Disclosure ---

  toggleDetalhes(passoIndex: number): void {
    this.detalhesExpandido[passoIndex] = !this.detalhesExpandido[passoIndex];
  }

  isDetalhesExpandido(passoIndex: number): boolean {
    return !!this.detalhesExpandido[passoIndex];
  }

  toggleInfoExcluidos(): void {
    this.mostrarInfoExcluidos = !this.mostrarInfoExcluidos;
  }

  // --- Helpers ---

  getBarWidth(indicacoes: number): string {
    const totalExaminadores = this.passoCorrente?.quadroExaminadores.length ?? 1;
    return `${(indicacoes / totalExaminadores) * 100}%`;
  }

  isClassificado(passo: ClassificacaoPassoDTO, inscricaoId: number | undefined): boolean {
    if (!inscricaoId) return false;
    if (passo.classificado?.inscricaoId === inscricaoId) return true;
    return passo.empatados?.some(e => e.inscricaoId === inscricaoId) ?? false;
  }

  getNomeClassificado(passo: ClassificacaoPassoDTO): string {
    if (passo.classificado) return passo.classificado.nome;
    if (passo.empatados?.length) return passo.empatados.map(e => e.nome).join(', ');
    return '';
  }

  getIndicacoesClassificado(passo: ClassificacaoPassoDTO): number {
    if (passo.classificado) return passo.classificado.quantidadeIndicacoes;
    if (passo.empatados?.length) return passo.empatados[0].quantidadeIndicacoes;
    return 0;
  }

  getCandidatoNaPos(ex: QuadroExaminadorDTO, posIdx: number): CandidatoRankDTO | null {
    return ex.candidatos[posIdx] ?? null;
  }

  temDesempateResolvido(passo: ClassificacaoPassoDTO): boolean {
    return passo.empate && passo.empatados?.some(e => !!e.conceito || !!e.justificativaDesempate);
  }

  getEmpatadosOrdenados(passo: ClassificacaoPassoDTO): ClassificadoDTO[] {
    if (!passo.empatados?.length) return [];
    return [...passo.empatados].sort((a, b) => {
      if (!a.conceito && !b.conceito) return 0;
      if (!a.conceito) return 1;
      if (!b.conceito) return -1;
      return a.conceito.localeCompare(b.conceito);
    });
  }

  getPosicaoAnterior(): string {
    if (this.passoAtual <= 0) return '';
    return this.dados.passos[this.passoAtual - 1].posicaoResultante.toString();
  }

  getPosicaoProxima(): string {
    if (this.passoAtual >= this.totalPassos - 1) return '';
    return this.dados.passos[this.passoAtual + 1].posicaoResultante.toString();
  }

  fechar(): void {
    this.dialogRef.close();
  }
}
